/**
 * ULID — Universally Unique Lexicographically Sortable Identifier.
 *
 * Every primary key in this system is a ULID, never a UUIDv4 and never
 * AUTOINCREMENT (dev brief §5.2 rule 2). Three properties earn it that place:
 *
 *   1. Timestamp-prefixed, so `ORDER BY id` is chronological and cursor
 *      pagination on `(occurred_on, id)` breaks ties sensibly.
 *   2. Lexicographically sortable as a plain TEXT column, which is all
 *      SQLite gives us.
 *   3. Generatable on a client with no server round-trip — the property that
 *      makes the future offline PWA possible without a schema rewrite.
 *
 * Implemented here rather than pulled from npm so that the identical code runs
 * in the Worker, in the Vue SPA and in tests, with no bundler-specific crypto
 * shim in the middle.
 *
 * Spec: https://github.com/ulid/spec
 */

/** Crockford's base32: no I, L, O or U, so a ULID can be read aloud. */
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ENCODING_LEN = 32;
const TIME_LEN = 10;
const RANDOM_LEN = 16;
const TIME_MAX = 281474976710655; // 2^48 - 1 — year 10889

/** A ULID is 26 Crockford-base32 characters. */
export type Ulid = string;

const ULID_RE = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

function encodeTime(now: number, len: number): string {
  if (!Number.isInteger(now) || now < 0) {
    throw new TypeError(`ULID timestamp must be a non-negative integer, got ${now}`);
  }
  if (now > TIME_MAX) {
    throw new RangeError(`ULID timestamp ${now} exceeds the 48-bit maximum`);
  }
  let out = '';
  for (let i = len; i > 0; i--) {
    const mod = now % ENCODING_LEN;
    out = ENCODING[mod] + out;
    now = (now - mod) / ENCODING_LEN;
  }
  return out;
}

function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  // Present in Workers, all modern browsers, and Node >=19.
  crypto.getRandomValues(buf);
  return buf;
}

function encodeRandom(len: number): string {
  const bytes = randomBytes(len);
  let out = '';
  for (let i = 0; i < len; i++) {
    // Modulo of a uniform byte over 32 is itself uniform: 256 = 8 * 32.
    out += ENCODING[bytes[i]! % ENCODING_LEN];
  }
  return out;
}

/**
 * Generate a ULID. Pass `seedTime` only in tests — production callers should
 * let it default so ids stay monotonic with wall-clock time.
 */
export function ulid(seedTime?: number): Ulid {
  const t = seedTime ?? Date.now();
  return encodeTime(t, TIME_LEN) + encodeRandom(RANDOM_LEN);
}

/**
 * Monotonic factory: guarantees strictly increasing ids even when called many
 * times within the same millisecond, by incrementing the random component
 * instead of regenerating it.
 *
 * Use this for bulk inserts — importing 625 positions or expanding a 480-line
 * work order — where plain `ulid()` would produce ids whose sort order does not
 * match insertion order.
 */
export function monotonicFactory(): (seedTime?: number) => Ulid {
  let lastTime = -1;
  let lastRandom: number[] = [];

  return function monotonicUlid(seedTime?: number): Ulid {
    const t = seedTime ?? Date.now();

    if (t <= lastTime) {
      // Same millisecond (or clock went backwards): increment the random part
      // as a big-endian base-32 number.
      let i = lastRandom.length - 1;
      for (; i >= 0; i--) {
        if (lastRandom[i]! < ENCODING_LEN - 1) {
          lastRandom[i]!++;
          break;
        }
        lastRandom[i] = 0;
      }
      if (i < 0) {
        // Overflowed all 80 bits within one millisecond. Astronomically
        // unlikely; reseed rather than emit a non-monotonic id.
        lastRandom = Array.from(randomBytes(RANDOM_LEN), (b) => b % ENCODING_LEN);
      }
      return encodeTime(lastTime, TIME_LEN) + lastRandom.map((n) => ENCODING[n]).join('');
    }

    lastTime = t;
    lastRandom = Array.from(randomBytes(RANDOM_LEN), (b) => b % ENCODING_LEN);
    return encodeTime(t, TIME_LEN) + lastRandom.map((n) => ENCODING[n]).join('');
  };
}

/** Shared monotonic generator — prefer this over `ulid()` for batch writes. */
export const ulidMonotonic = monotonicFactory();

/** True if `value` is a syntactically valid ULID. */
export function isUlid(value: unknown): value is Ulid {
  return typeof value === 'string' && value.length === 26 && ULID_RE.test(value);
}

/** Extract the creation timestamp (ms since epoch) encoded in a ULID. */
export function ulidTime(id: Ulid): number {
  if (!isUlid(id)) throw new TypeError(`Not a ULID: ${id}`);
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) {
    t = t * ENCODING_LEN + ENCODING.indexOf(id[i]!);
  }
  return t;
}

/** Extract the creation time as an ISO-8601 UTC string. */
export function ulidDate(id: Ulid): string {
  return new Date(ulidTime(id)).toISOString();
}
